import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchInvoicesByLab } from "../../store/superAdmin/invoicesSlice";
import { InvoiceType } from "../../types/InvoicesTypes";
import { downloadBoxFile } from "../../components/utilities/downloadFiles";
import { fetchBoxAccessToken } from "../../store/common/authSlice";
import { Helmet } from "react-helmet-async";
import { isUrisBrand } from "../../utils/brandLogo";

const MyInvoices = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const isUris = isUrisBrand(user);
  const { userInvoices, loading, error } = useAppSelector(
    (state) => state.invoices
  );
  const { boxAccessToken } = useAppSelector((state) => state.auth);

  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (isUris) return;
    dispatch(fetchBoxAccessToken());
  }, [dispatch, isUris]);

  useEffect(() => {
    if (isUris || !user?.token) return;
    dispatch(fetchInvoicesByLab(user.token));
  }, [dispatch, user?.token, isUris]);

  if (isUris) {
    return (
      <Navigate
        to={
          user?.role === "Side Admin"
            ? "/dashboard/admin/cases"
            : "/dashboard/my-cases"
        }
        replace
      />
    );
  }

  const handleDownloadFile = async (fileId: string, fileName: string) => {
    if (!boxAccessToken) return;
    setDownloadingFileId(fileId);
    await downloadBoxFile(fileId, fileName, boxAccessToken);
    setDownloadingFileId(null);
  };

  return (
    <>
      <Helmet>
        <title>Invoices</title>
      </Helmet>
      <div className={isUris ? "text-white" : "p-4"}>
        <h2 className={`text-2xl font-semibold mb-4 ${isUris ? "text-white" : ""}`}>
          My Invoices
        </h2>

        {loading && (
          <p className={isUris ? "text-[#c5c7cc]" : ""}>Loading invoices...</p>
        )}
        {error && (
          <p className={isUris ? "text-[#ff8b96]" : "text-red-500"}>{error}</p>
        )}
        {!loading && userInvoices.length === 0 && (
          <p className={isUris ? "text-[#c5c7cc]" : ""}>No invoices found.</p>
        )}

        {userInvoices.length > 0 && (
          <div
            className={
              isUris
                ? "overflow-x-auto rounded-2xl border border-[#6b2430] bg-[#141517] p-3"
                : "overflow-x-auto"
            }
          >
            <table
              className={
                isUris
                  ? "min-w-full border-separate border-spacing-y-2"
                  : "min-w-full border border-gray-300"
              }
            >
              <thead className={isUris ? "" : "bg-gray-100"}>
                <tr>
                  {[
                    "Invoice Number",
                    "Billing Period",
                    "Amount",
                    "Work Summary",
                    "Status",
                    "Payment Link",
                  ].map((heading) => (
                    <th
                      key={heading}
                      className={
                        isUris
                          ? "px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[#8b8d93]"
                          : "px-4 py-2 border"
                      }
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {userInvoices.map((invoice: InvoiceType) => {
                  const fileIds = invoice.work_Summary?.split(",") || [];
                  const cell = isUris
                    ? "bg-[#1d1e22] px-4 py-3 text-[#c5c7cc] first:rounded-l-xl last:rounded-r-xl"
                    : "px-4 py-2 border";
                  const paid = invoice?.status === "Paid";
                  return (
                    <tr key={invoice.invoice_Number} className="text-center">
                      <td className={`${cell} ${isUris ? "text-[#ff6b81]" : ""}`}>
                        {invoice.invoice_Number}
                      </td>
                      <td className={cell}>{invoice.billing_period}</td>
                      <td className={cell}>{invoice.amount_due}</td>
                      <td className={cell}>
                        <div className="flex flex-col">
                          {fileIds.map((fileId, index) => (
                            <button
                              key={fileId}
                              onClick={() =>
                                handleDownloadFile(
                                  fileId,
                                  fileIds.length === 1
                                    ? "Summary"
                                    : `Summary ${index + 1}`
                                )
                              }
                              className={`underline mr-2 mb-1 ${
                                isUris ? "text-[#ff8b96]" : "text-blue-500"
                              }`}
                              disabled={downloadingFileId === fileId}
                            >
                              {downloadingFileId === fileId
                                ? "Downloading..."
                                : fileIds.length === 1
                                ? "Summary"
                                : `Summary ${index + 1}`}
                            </button>
                          ))}
                        </div>
                      </td>

                      <td className={cell}>
                        {isUris ? (
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                              paid
                                ? "bg-[#163226] text-[#7dffa8]"
                                : "bg-[#3a1520] text-[#ff8b96]"
                            }`}
                          >
                            {invoice.status}
                          </span>
                        ) : (
                          invoice.status
                        )}
                      </td>
                      <td className={cell}>
                        <button
                          disabled={paid}
                          onClick={() => {
                            window.open(invoice.invoice_link, "_blank");
                          }}
                          className={`py-1 px-4 rounded-full text-white ${
                            paid
                              ? isUris
                                ? "cursor-not-allowed bg-[#163226] text-[#7dffa8]"
                                : "cursor-not-allowed bg-green-500"
                              : isUris
                                ? "bg-[#e11d2e] hover:bg-[#c31c30]"
                                : "bg-blue-500 hover:bg-blue-600"
                          }`}
                        >
                          {paid ? "Paid" : "Pay Now"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default MyInvoices;
